<template>
  <v-container fluid class="pa-0">
    <!-- Header: Title + Credits -->
    <div class="jd-header d-flex align-center justify-space-between mb-2">
      <div class="text-h6 font-weight-medium">Jobs Dashboard</div>
      <v-btn variant="outlined" color="#B8860B" class="text-none" elevation="2">
        {{ credits }} Credits Remaining
        <v-icon right>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Search row -->
    <div class="jd-search-row d-flex justify-end mb-6">
      <v-text-field
        v-model="search"
        placeholder="Search by job title"
        hide-details
        dense
        variant="outlined"
        append-inner-icon="mdi-magnify"
        style="max-width: 300px; height: 40px !important"
      />
    </div>

    <!-- Status Tabs (mobile: horizontally scrollable) -->
   <div class="jd-tabs-scroll">
  <div class="jd-tabs-track">
    <v-btn-toggle v-model="activeTab" mandatory group class="jd-tabs-toggle mb-6">
      <v-btn
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
        depressed
        class="text-none"
        :class="activeTab === tab.value ? getTabClass(tab.value) : 'grey--text text--darken-1 white--bg'"
        style="text-transform: none; border-radius: 999px; margin-right: 8px"
      >
        {{ tab.label }} ({{ tab.count }})
      </v-btn>
    </v-btn-toggle>
  </div>
</div>
    <!-- Empty State Placeholder -->
    <div v-if="filteredJobs.length === 0">
      <v-card elevation="1" class="pa-12 text-center mb-6">
        <v-img
          src="@/assets/jobs-dashboard-empty.svg"
          contain
          max-width="300"
          class="mx-auto mb-6"
        />
        <div class="body-1 mb-2">
          There are no {{ currentTabLabel }} listings.
        </div>
        <div class="body-2 mb-6">
          Please refine your search using the filters above.
        </div>
        <v-btn
          style="text-transform: none"
          color="#B8860B"
          dark
          @click="postJob"
          :disabled="!profileDone"
          :color="profileDone ? '#B8860B' : ''"
        >
          <v-icon left>mdi-plus</v-icon>
          Post A Job
        </v-btn>
      </v-card>
    </div>

    <!-- Job Cards List -->
    <div v-else>
      <v-row dense>
        <v-col cols="12" v-for="job in pagedJobs" :key="job.id">
          <v-card
            outlined
            elevation="1"
            class="pa-4 mb-4 jobs-display-card"
            @click="openApps(job)"
            style="cursor: pointer"
          >
            <v-row>
              <!-- Title & Company -->
              <v-col cols="12" md="4">
                <div class="subtitle-1 font-weight-medium">{{ job.title }}</div>
                <div class="body-2 grey--text">{{ job.company_name }}</div>
              </v-col>

              <!-- Salary & Location -->
              <v-col cols="12" md="3">
                <div class="d-flex align-center mb-2">
                  <v-icon class="mr-1">mdi-cash</v-icon>
                  {{ job.salary_expectations }}
                </div>
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-map-marker</v-icon>
                  {{ job.current_location }}
                </div>
              </v-col>

              <!-- Commitment & Posted Date -->
              <v-col cols="12" md="3">
                <div class="d-flex align-center mb-2">
                  <v-icon class="mr-1">mdi-briefcase</v-icon>
                  {{ job.commitment }}
                </div>
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-calendar</v-icon>
                  Posted {{ formatDate(job.created_at) }}
                </div>
              </v-col>

              <!-- Actions (reserved) -->
              <v-col cols="12" md="2" class="d-flex align-center justify-end">
                <!-- Reserved for actions -->
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <div class="d-flex justify-center mt-6">
        <v-pagination v-model="page" :length="totalPages" circle />
      </div>
    </div>

    <!-- Applications Dialog -->
    <v-dialog
      v-model="appsDialog"
      max-width="1100"
      scrollable
      :fullscreen="display.smAndDown"
      transition="dialog-bottom-transition"
    >
      <v-card class="pa-4">
        <div class="d-flex align-center mb-2">
          <div class="text-h6 font-weight-bold">
            Applications — {{ activeJob?.title || "" }}
          </div>
          <v-chip class="ml-2" size="small" color="primary" variant="flat">
            {{ applicationsCount }}
            {{ applicationsCount === 1 ? "application" : "applications" }}
          </v-chip>
          <v-spacer />
          <v-btn icon @click="appsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- controls -->
        <v-card class="pa-3 mb-3" variant="outlined">
          <div class="d-flex align-center flex-wrap">
            <v-text-field
              v-model="appsSearch"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="Search name, email, company..."
              class="ml-auto"
              style="max-width: 280px"
            />
          </div>
        </v-card>

        <!-- list -->
        <v-card variant="outlined">
          <!-- Mobile card list -->
          <div v-if="display.smAndDown" class="apps-mobile-list">
            <v-card
              v-for="app in limitedApps"
              :key="app.id"
              variant="outlined"
              class="mb-2 pa-3"
            >
              <div class="d-flex align-center">
                <v-avatar size="36" color="grey-lighten-4" class="mr-3">
                  <span class="text-caption">{{ initials(app.fullName) }}</span>
                </v-avatar>
                <div class="flex-1 min-w-0">
                  <div class="font-weight-medium text-truncate">
                    {{ app.fullName }}
                  </div>
                  <div class="text-caption grey--text text-truncate">
                    {{ app.current_position || "—" }} •
                    {{ app.current_company || "—" }}
                  </div>
                </div>
                <div class="text-caption grey--text ml-2">
                  {{ formatAppDate(app.created_at) }}
                </div>
              </div>

              <v-expand-transition>
                <div v-show="rowDetailsOpen.has(app.id)" class="mt-2">
                  <v-list density="compact">
                    <v-list-item
                      title="Location"
                      :subtitle="app.current_location || '—'"
                    />
                    <v-list-item
                      title="Education"
                      :subtitle="app.educationLevel || '—'"
                    />
                    <v-list-item
                      title="Experience"
                      :subtitle="app.min_work_experience || '—'"
                    />
                    <v-list-item title="Email" :subtitle="app.email || '—'" />
                    <v-list-item
                      title="Phone"
                      :subtitle="app.phoneNumber || '—'"
                    />
                  </v-list>
                  <div class="d-flex mt-1">
                    <v-btn
                      v-if="app.cv_path"
                      size="small"
                      variant="text"
                      :href="cvUrl(app.cv_path)"
                      target="_blank"
                      icon="mdi-file-download-outline"
                      class="mr-1"
                    />
                    <v-btn
                      v-if="app.email"
                      size="small"
                      variant="text"
                      :href="`mailto:${app.email}`"
                      icon="mdi-email-outline"
                      class="mr-1"
                    />
                    <v-btn
                      v-if="app.phoneNumber"
                      size="small"
                      variant="text"
                      :href="`tel:${normalizePhone(app.phoneNumber)}`"
                      icon="mdi-phone-outline"
                      class="mr-1"
                    />
                  </div>
                </div>
              </v-expand-transition>

              <div class="d-flex justify-end mt-1">
                <v-btn
                  size="small"
                  variant="text"
                  :icon="
                    rowDetailsOpen.has(app.id)
                      ? 'mdi-chevron-up'
                      : 'mdi-chevron-down'
                  "
                  @click="toggleDetails(app.id)"
                />
              </div>
            </v-card>

            <div
              class="d-flex justify-center py-2"
              v-if="filteredApps.length > limit"
            >
              <v-btn
                size="small"
                variant="text"
                @click="showAllApps = !showAllApps"
              >
                {{ showAllApps ? "Show less" : `Show all ${filteredApps.length}` }}
              </v-btn>
            </div>

            <div v-if="!limitedApps.length" class="text-center py-6 grey--text text-caption">
              No applications match your search.
            </div>
          </div>

          <!-- Desktop/table view (unchanged) -->
          <div v-else>
            <div style="max-height: 420px; overflow: auto">
              <v-table density="compact" fixed-header>
                <thead>
                  <tr>
                    <th style="width: 36px"></th>
                    <th>Name / Role</th>
                    <th>Company</th>
                    <th>Experience</th>
                    <th>Location</th>
                    <th>Education</th>
                    <th>Applied</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="app in limitedApps" :key="app.id">
                    <td>
                      <v-avatar size="28" color="grey-lighten-3">
                        <span class="text-caption">{{ initials(app.fullName) }}</span>
                      </v-avatar>
                    </td>
                    <td>
                      <div class="font-weight-medium">{{ app.fullName }}</div>
                      <div class="text-caption grey--text">
                        {{ app.current_position || "—" }}
                      </div>
                    </td>
                    <td class="text-caption">{{ app.current_company || "—" }}</td>
                    <td>
                      <v-chip size="x-small" variant="outlined">
                        {{ app.min_work_experience || "—" }}
                      </v-chip>
                    </td>
                    <td class="text-caption">
                      {{ app.current_location || "—" }}
                    </td>
                    <td class="text-caption">{{ app.educationLevel || "—" }}</td>
                    <td class="text-caption">
                      {{ formatAppDate(app.created_at) }}
                    </td>
                    <td class="text-right">
                      <v-btn
                        v-if="app.cv_path"
                        size="x-small"
                        variant="text"
                        :href="cvUrl(app.cv_path)"
                        target="_blank"
                        icon="mdi-file-download-outline"
                        class="mr-1"
                      />
                      <v-btn
                        v-if="app.email"
                        size="x-small"
                        variant="text"
                        :href="`mailto:${app.email}`"
                        icon="mdi-email-outline"
                        class="mr-1"
                      />
                      <v-btn
                        v-if="app.phoneNumber"
                        size="x-small"
                        variant="text"
                        :href="`tel:${normalizePhone(app.phoneNumber)}`"
                        icon="mdi-phone-outline"
                        class="mr-1"
                      />
                      <v-btn
                        size="x-small"
                        variant="text"
                        icon="mdi-eye-outline"
                        @click="toggleDetails(app.id)"
                      />
                    </td>
                  </tr>

                  <!-- inline details -->
                  <tr v-for="app in detailsOpenApps" :key="`d-${app.id}`">
                    <td></td>
                    <td colspan="7" class="py-2">
                      <v-expand-transition>
                        <v-card class="pa-3" variant="tonal">
                          <v-row dense>
                            <v-col cols="12" md="6">
                              <v-list density="compact">
                                <v-list-item title="Email" :subtitle="app.email || '—'" />
                                <v-list-item title="Phone" :subtitle="app.phoneNumber || '—'" />
                                <v-list-item title="Nationality" :subtitle="app.nationality || '—'" />
                                <v-list-item title="Gender" :subtitle="app.gender || '—'" />
                              </v-list>
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-list density="compact">
                                <v-list-item title="Notice Period" :subtitle="app.notice_period || '—'" />
                                <v-list-item title="Visa Status" :subtitle="app.visaStatus || '—'" />
                                <v-list-item title="Job Status" :subtitle="app.jobStatus || '—'" />
                              </v-list>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-expand-transition>
                    </td>
                  </tr>

                  <tr v-if="!limitedApps.length">
                    <td colspan="8" class="text-center py-6 grey--text text-caption">
                      No applications match your search.
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <v-divider />
            <div class="d-flex justify-center py-2" v-if="filteredApps.length > limit">
              <v-btn size="small" variant="text" @click="showAllApps = !showAllApps">
                {{ showAllApps ? "Show less" : `Show all ${filteredApps.length}` }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-card>
    </v-dialog>
  </v-container>

   
</template>


<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { all } from "axios";
import moment from "moment";
import { API_URL } from "@/helpers/api.js"; // if you have it
import { useDisplay } from 'vuetify';
import FooterMobile from "../footer/FooterMobile.vue";

dayjs.extend(relativeTime);

const store = useStore();
const router = useRouter();

const display = useDisplay();

// State
const credits = ref(0);
const search = ref("");
const activeTab = ref("live");
const page = ref(1);
const perPage = 10;

// Current tab label for empty state
const currentTabLabel = computed(() => {
  const t = tabs.value.find((t) => t.value === activeTab.value);
  return t ? t.label : "";
});

const personalDone = computed(() => !!store.getters["personalProfile/profile"]);
const companyDone = computed(
  () => !!store.getters["companyProfile/companyProfile"] // or 'company' if you rename it
);
const profileDone = computed(() => personalDone.value || companyDone.value);

// Tabs config (counts will update once jobs load)
const tabs = computed(() => [
  { value: "live", label: "Live", count: jobsByStatus("live").length },
  { value: "draft", label: "Drafts", count: jobsByStatus("draft").length },
  {
    value: "pending",
    label: "Pending Payment",
    count: jobsByStatus("pending").length,
  },
  {
    value: "review",
    label: "Under Review",
    count: jobsByStatus("review").length,
  },
  {
    value: "rejected",
    label: "Rejected",
    count: jobsByStatus("rejected").length,
  },
  { value: "expired", label: "Expired", count: jobsByStatus("expired").length },
]);

// Function to assign different colors per tab
function getTabClass(value) {
  switch (value) {
    case "live":
      return "green lighten-4 black--text";
    case "draft":
      return "blue lighten-4 black--text";
    case "pending":
      return "orange lighten-4 black--text";
    case "review":
      return "purple lighten-4 black--text";
    case "rejected":
      return "red lighten-4 black--text";
    case "expired":
      return "grey lighten-4 black--text";
    default:
      return "grey--text";
  }
}

// Fetch on mount
onMounted(() => {
  store.dispatch("jobs/fetchJobs");

  console.log("Fetching jobs from store");

  console.log("fetched jobs:", allJobs.value);
});

// Get all jobs from store
const allJobs = computed(() => store.getters["jobs/allJobs"] || []);

// Helper to filter by tab
function jobsByStatus(status) {
  if (status === "live")
    return allJobs.value.filter((j) => j.listing.status === 1);
  if (status === "draft")
    return allJobs.value.filter((j) => j.listing.status === 0);
  if (status === "pending")
    return allJobs.value.filter((j) => j.listing.status === 2);
  if (status === "rejected")
    return allJobs.value.filter((j) => j.listing.status === 3);

  if (status === "review")
    return allJobs.value.filter((j) => j.listing.status === 2);
  //will chnage later
  if (status === "expired")
    return allJobs.value.filter((j) => j.listing.status === 4);
  return allJobs.value;
}

// Filter by tab & search
const filteredJobs = computed(() =>
  jobsByStatus(activeTab.value).filter((j) =>
    j.title.toLowerCase().includes(search.value.toLowerCase())
  )
);

// Pagination
const totalPages = computed(() =>
  Math.ceil(filteredJobs.value.length / perPage)
);
const pagedJobs = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredJobs.value.slice(start, start + perPage);
});

// Formatting & actions
function formatDate(date) {
  return dayjs(date).fromNow();
}
function viewJob(id) {
  router.push({ name: "JobDetails", params: { id } });
}
function postJob() {
  router.push({ name: "SelectYourCityForJobAd" });
}

// dialog state
const appsDialog = ref(false);
const activeJob = ref(null); // the clicked job object (from allJobs)

// open from card
function openApps(job) {
  activeJob.value = job;
  appsDialog.value = true;
}

// ===== Applications data (normalized from allJobs item) =====
const applications = computed(() => {
  // your allJobs items seem to have apps in either job or details
  const j = activeJob.value;
  if (!j) return [];
  return j.applications || j.job?.applications || j.details?.applications || [];
});
const applicationsCount = computed(() => applications.value.length);
const hasApplications = computed(() => applicationsCount.value > 0);

// same helpers as in the listing dialog
const appsSearch = ref("");
const showAllApps = ref(false);
const rowDetailsOpen = ref(new Set());
const limit = 10;

const filteredApps = computed(() => {
  const q = appsSearch.value.trim().toLowerCase();
  if (!q) return applications.value;
  return applications.value.filter((a) =>
    [
      a.fullName,
      a.email,
      a.current_company,
      a.current_position,
      a.current_location,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q))
  );
});
const limitedApps = computed(() =>
  showAllApps.value ? filteredApps.value : filteredApps.value.slice(0, limit)
);
const detailsOpenApps = computed(() =>
  limitedApps.value.filter((a) => rowDetailsOpen.value.has(a.id))
);

function toggleDetails(id) {
  if (rowDetailsOpen.value.has(id)) rowDetailsOpen.value.delete(id);
  else rowDetailsOpen.value.add(id);
  rowDetailsOpen.value = new Set(rowDetailsOpen.value); // trigger reactivity
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("");
}
function normalizePhone(p) {
  return String(p || "").replace(/[^\d+]/g, "");
}
function formatAppDate(dt) {
  return dt ? moment(dt).format("D MMM YYYY, HH:mm") : "—";
}
function cvUrl(path) {
  if (!path) return "";
  return path.startsWith("http") ? path : `${API_URL}/storage/${path}`;
}
</script>

<style scoped>
.jobs-search {
  max-width: 300px;
}


/* ===== Mobile-only layout polish (desktop unaffected) ===== */
@media (max-width: 768px) {
  /* Header stacks; credits button full width */
  .jd-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px;
  }
  .jd-header .v-btn {
    width: 100%;
    justify-content: center;
  }

  /* Search: full width */
  .jd-search-row {
    justify-content: stretch !important;
    margin-bottom: 12px !important;
  }
  .jd-search-row .v-text-field {
    max-width: none !important;
    width: 100% !important;
  }

  /* Tabs: horizontal scroll pills */
  .jd-tabs-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -18px 17px;
    padding: 0 12px 6px;
    margin-top: 30px !important;
    
  }
  .jd-tabs-scroll .v-btn-toggle {
    display: inline-flex;
    gap: 8px;
  }
  .jd-tabs-scroll .v-btn-toggle .v-btn {
    flex: 0 0 auto;
    
    border-radius: 999px !important;
    padding: 0 12px;
  }

  /* Job cards: tighter spacing for mobile */
  .jobs-display-card {
    padding: 12px !important;
  }
  .jobs-display-card .v-col {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
  .jobs-display-card .subtitle-1 { font-size: 1rem; }
  .jobs-display-card .body-2 { font-size: .9rem; }
  .jobs-display-card .v-icon { font-size: 18px; margin-right: 6px !important; }

  /* Apps dialog in fullscreen: remove rounding */
  :deep(.v-dialog--fullscreen) .v-card {
    border-radius: 0 !important;
  }

  /* Truncation helpers inside mobile app cards */
  .apps-mobile-list .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}


@media (max-width: 768px) {


  /* Optional: hide scrollbar */
  .jd-tabs-scroll::-webkit-scrollbar { display: none; }
  .jd-tabs-scroll { scrollbar-width: none; }

  /* Make inner content only as wide as needed so it can overflow */
  .jd-tabs-track {
    display: inline-block;         /* shrink-wrap to contents */
    white-space: nowrap;           /* prevent wrapping */
  }

  /* Prevent wrapping & shrinking of buttons */
  .jd-tabs-toggle {
    display: inline-flex;          /* sit inline to allow overflow */
    flex-wrap: nowrap;
    width: auto;
    gap: 8px;
  }
  .jd-tabs-toggle .v-btn {
    flex: 0 0 auto;                /* don't shrink */
    padding: 0 12px;
  }
}

</style>
